import dayjs from 'dayjs';
import { emptyArray, emptyNull } from '../../../shared/misc';
import { getUseRegExpInPage } from './state';
import { isActiveById, getSettingsById } from '../../utils';
import { MODULE_NAME } from './constants';

export const isActive = () => isActiveById(MODULE_NAME);
export const getSettings = () => getSettingsById(MODULE_NAME);

const isResourceTypePage = (type) => type === 'Page';
const coerceToArray = (x) => (Array.isArray(x) ? x : [x]);
const strictEqualStr = (x) => (y) => x.toLowerCase() === y.toLowerCase();
const regexpEqual = (x) => (y) => new RegExp(y, 'i').test(x);

const bySameType = (resourceType) => (resource) => resourceType.toLowerCase() === resource.Type.toLowerCase();

const bySameIdentifier = (resourceType) => (resourceId) => (resource) => {
  const resourceIdentifier = coerceToArray(resource.Identifier);
  let isSameIdentifier = strictEqualStr(resourceId);

  if (isResourceTypePage(resourceType) && getUseRegExpInPage()) isSameIdentifier = regexpEqual(resourceId);

  return resourceIdentifier.some(isSameIdentifier);
};

export const getPoliciesForResource = (resourceType, resourceId, accessMatrix) => {
  const getResource = () => {
    const foundResources = accessMatrix.resources
      .filter(bySameType(resourceType))
      .filter(bySameIdentifier(resourceType)(resourceId));

    if (foundResources.length > 1) {
      // DEBUG: for now just print a WARNING
      console.warn(
        `WARNING: multiple resource access definitions for Resource:${resourceType}id:${resourceId}`
      );
    }
    if (foundResources.length > 0) {
      return foundResources[0];
    }
    return null;
  };

  const foundResource = getResource();
  if (!foundResource) return [];

  const returnedPolicies = [];

  foundResource.PoliciesIds.forEach((policyId) => {
    const foundPolicy = accessMatrix.policies.find((p) => p.id === policyId);
    if (foundPolicy) returnedPolicies.push(foundPolicy);
  });

  return returnedPolicies;
};

export const getDenyAllowPoliciesForResource = (resourceType, resourceId, accessMatrix) => {
  const denyAllowRules = {
    deny: [],
    allow: []
  };

  const foundPolicies = getPoliciesForResource(resourceType, resourceId, accessMatrix);
  for (const policy of foundPolicies) {
    if (policy.Effect === 'Allow') {
      denyAllowRules.allow.push(policy);
    }
    if (policy.Effect === 'Deny') {
      denyAllowRules.deny.push(policy);
    }
  }
  return denyAllowRules;
};

export const handleIncludes = (param) => {
  const {
    userField, requirement, accessDenyReasons, evaluatedPermissionsArray
  } = param;
  const values = Array.isArray(requirement.Values) ? requirement.Values : [requirement.Values];
  // Coerce both sides to string so boolean user fields (e.g. participantTypeValid)
  // match string values stored by the admin UI (e.g. ["true"]).
  const passed = values.some((v) => String(v) === String(userField));
  if (!passed) {
    accessDenyReasons.push(requirement.PrincipalField);
  }
  evaluatedPermissionsArray.push(passed);
};

// Original (Vue) ParticipantTypeCheck semantics: BOTH the participant type
// name must be in Values AND user.participantTypeValid must be true. The
// earlier React port only checked the boolean, which silently dropped the
// type-name check.
export const handleParticipantTypeCheck = (param) => {
  const {
    participantType, participantTypeValid, requirement, accessDenyReasons, evaluatedPermissionsArray
  } = param;
  const values = Array.isArray(requirement.Values) ? requirement.Values : [requirement.Values];
  const typeMatches = values.some((v) => String(v) === String(participantType));
  const validMatches = participantTypeValid === true || String(participantTypeValid) === 'true';
  const passed = typeMatches && validMatches;
  if (!passed) {
    accessDenyReasons.push(requirement.PrincipalField);
  }
  evaluatedPermissionsArray.push(passed);
};

export const handleHcp = (user, requirement, accessDenyReasons, evaluatedPermissionsArray) => {
  const hcpVal = requirement.Values?.[0];
  let elEval = false;
  // @ts-ignore
  if (hcpVal === null || hcpVal === true) {
    if (user.hcp) {
      elEval = true;
    }
    // @ts-ignore
  } else if (hcpVal === false) {
    if (user.hcp === false) {
      elEval = true;
    }
  }
  if (!elEval) {
    accessDenyReasons.push(requirement.PrincipalField);
  }
  evaluatedPermissionsArray.push(elEval);
};

export const handleLoggedIn = (user, requirement, accessDenyReasons, evaluatedPermissionsArray) => {
  // Values[0] may arrive as boolean true/false (newer admin UI) or the
  // strings 'true'/'false' (legacy). Normalize to a boolean expectation.
  const raw = requirement.Values?.[0];
  const expected = raw === true || raw === 'true';
  const passed = !!user.loggedIn === expected;
  if (!passed) {
    accessDenyReasons.push(requirement.PrincipalField);
  }
  evaluatedPermissionsArray.push(passed);
};

export const handleTicketCondition = (params) => {
  const {
    userTickets, conditionFn, requirement, accessDenyReasons, evaluatedPermissionsArray
  } = params;
  let valid = false;
  for (const ticket of userTickets) {
    if (conditionFn(ticket)) {
      valid = true;
      break;
    }
  }
  if (!valid) {
    accessDenyReasons.push(requirement.PrincipalField);
  }
  evaluatedPermissionsArray.push(valid);
};

// Scan all tickets for the first one that has a non-null/non-empty value for the
// given field. The BookedTickets payload does not guarantee that profile fields
// (ParticipantTypeName, SocietyMemberType, …) live on tickets[0] — they may
// only exist on a later ticket, or not at all. See data-insight/model/utils.js
// `getFromBookedTickets` for the same pattern.
const pickFromTickets = (tickets, field) => {
  if (!Array.isArray(tickets)) return undefined;
  const hit = tickets.find((t) => t != null && t[field] != null && t[field] !== '');
  return hit ? hit[field] : undefined;
};

export const composeUser = (
  tickets,
  { loggedIn = false, extendedUser = emptyNull } = {}
) => {
  const eu = extendedUser ?? emptyNull;

  // hcp: CoreUser exposes `HealthCareProfessional`; BookedTickets uses `EnableHCP`.
  const hcp = eu.HealthCareProfessional
    ?? eu.EnableHCP
    ?? pickFromTickets(tickets, 'EnableHCP')
    ?? false;

  const participantType = eu.ParticipantTypeName
    ?? pickFromTickets(tickets, 'ParticipantTypeName')
    ?? '';

  const participantTypeValid = eu.ParticipantTypeValid
    ?? pickFromTickets(tickets, 'ParticipantTypeValid')
    ?? false;

  const societyMemberType = eu.SocietyMemberType
    ?? pickFromTickets(tickets, 'SocietyMemberType')
    ?? '';

  const country = eu.Country
    ?? eu.CountryCode
    ?? pickFromTickets(tickets, 'Country')
    ?? '';

  return {
    hcp,
    participantType,
    participantTypeValid,
    societyMemberType,
    country,
    tickets: tickets ?? emptyArray,
    todayDate: null,
    loggedIn
  };
};

export const fallbackToPathname = (location) => location.pathname;

export const evaluateTimeAccess = ({ values = [], now }) => {
  if (!Array.isArray(values) || values.length === 0) {
    return false;
  }
  if (values.length > 2) {
    throw new Error(`TimeAccess: ⟨values⟩ must have max two timestamps ${values.toString()}`);
  }

  const [rawStart, rawEnd] = values;
  const start = rawStart ? dayjs(rawStart) : null;
  const end = rawEnd ? dayjs(rawEnd) : null;

  if ((start && !start.isValid()) || (end && !end.isValid())) {
    throw new Error(`TimeAccess: Invalid date string in values ${values.toString()}`);
  }
  if (start && end && start.isAfter(end)) {
    throw new Error(`TimeAccess: Start date is after end date ${values.toString()}`);
  }

  let insideWindow;

  if (values.length === 1) {
    //  Allow => allow from that moment on
    //  Deny => deny from that moment on
    insideWindow = now.isSame(start) || now.isAfter(start);
  } else {
    const afterStart = !start || now.isSame(start) || now.isAfter(start);
    const beforeEnd = !end || now.isSame(end) || now.isBefore(end);
    insideWindow = afterStart && beforeEnd;
  }

  return insideWindow;
};
